import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Trophy,
  Medal,
  Flame,
  Search,
  School,
  Crown,
  Loader2,
  ChevronsUpDown,
  Check,
  X,
  GraduationCap,
  MapPin
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exam years (2024-2030)
const EXAM_YEARS = Array.from({ length: 7 }, (_, i) => 2024 + i);

const Leaderboard = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState('daily');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [streams, setStreams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schoolFilter, setSchoolFilter] = useState('');
  const [streamFilter, setStreamFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [examYearFilter, setExamYearFilter] = useState('all');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [schoolOpen, setSchoolOpen] = useState(false);

  const fetchSchools = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/schools`);
      setSchools(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch schools');
      setSchools([]);
    }
  }, []);

  const fetchDistricts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/districts`);
      setDistricts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch districts');
      setDistricts([]);
    }
  }, []);

  const fetchStreams = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/streams`);
      setStreams(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch streams');
      setStreams([]);
    }
  }, []);

  const fetchSubjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      setSubjects(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch subjects');
      setSubjects([]);
    }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/leaderboard/${period}`;
      const params = new URLSearchParams();
      if (schoolFilter) params.append('school', schoolFilter);
      if (streamFilter && streamFilter !== 'all') params.append('stream', streamFilter);
      if (subjectFilter && subjectFilter !== 'all') params.append('subject', subjectFilter);
      if (examYearFilter && examYearFilter !== 'all') params.append('exam_year', examYearFilter);
      if (districtFilter && districtFilter !== 'all') params.append('district', districtFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setEntries(response.data.entries || []);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [period, schoolFilter, streamFilter, subjectFilter, examYearFilter, districtFilter]);

  useEffect(() => {
    fetchSchools();
    fetchDistricts();
    fetchStreams();
    fetchSubjects();
  }, [fetchSchools, fetchDistricts, fetchStreams, fetchSubjects]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const handleFilter = () => {
    fetchLeaderboard();
  };

  const clearSchoolFilter = () => {
    setSchoolFilter('');
  };

  const clearAllFilters = () => {
    setSchoolFilter('');
    setStreamFilter('all');
    setSubjectFilter('all');
    setExamYearFilter('all');
    setDistrictFilter('all');
  };

  const hasActiveFilters = schoolFilter || streamFilter !== 'all' || subjectFilter !== 'all' || examYearFilter !== 'all' || districtFilter !== 'all';

  const selectedSchool = Array.isArray(schools) ? schools.find(s => s.display_name === schoolFilter) : null;

  const getRankBadge = (rank) => {
    switch (rank) {
      case 1:
        return (
          <div className="rank-badge rank-1">
            <Crown className="w-5 h-5" />
          </div>
        );
      case 2:
        return (
          <div className="rank-badge rank-2">
            <Medal className="w-5 h-5" />
          </div>
        );
      case 3:
        return (
          <div className="rank-badge rank-3">
            <Medal className="w-5 h-5" />
          </div>
        );
      default:
        return (
          <div className="rank-badge bg-secondary">
            <span>{rank}</span>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="leaderboard-page">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <Trophy className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">
            Leaderboard
          </h1>
          <p className="text-muted-foreground">
            See how you stack up against other students
          </p>
        </div>

        {/* Period Tabs */}
        <Tabs value={period} onValueChange={setPeriod} className="mb-8">
          <TabsList className="grid grid-cols-4 w-full max-w-md mx-auto">
            <TabsTrigger value="daily" data-testid="period-daily">Daily</TabsTrigger>
            <TabsTrigger value="weekly" data-testid="period-weekly">Weekly</TabsTrigger>
            <TabsTrigger value="monthly" data-testid="period-monthly">Monthly</TabsTrigger>
            <TabsTrigger value="all-time" data-testid="period-alltime">All Time</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-4 mb-8">
          <div className="flex flex-wrap gap-4">
            {/* District Filter */}
            <div className="flex-1 min-w-[150px]">
              <Select value={districtFilter} onValueChange={setDistrictFilter}>
                <SelectTrigger data-testid="district-filter-select">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <SelectValue placeholder="All Districts" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Districts</SelectItem>
                  {districts.map((district) => (
                    <SelectItem key={district} value={district}>{district}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* School Filter */}
            <div className="flex-1 min-w-[200px]">
              <Popover open={schoolOpen} onOpenChange={setSchoolOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={schoolOpen}
                    className="w-full justify-between font-normal"
                    data-testid="school-filter-select"
                  >
                    <div className="flex items-center gap-2">
                      <School className="w-4 h-4 text-muted-foreground" />
                      {schoolFilter ? schoolFilter : "All Schools"}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search school..." />
                    <CommandList>
                      <CommandEmpty>No school found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all-schools"
                          onSelect={() => {
                            setSchoolFilter('');
                            setSchoolOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              !schoolFilter ? "opacity-100" : "opacity-0"
                            )}
                          />
                          All Schools
                        </CommandItem>
                        {schools.map((school) => (
                          <CommandItem
                            key={school.id}
                            value={school.display_name}
                            onSelect={() => {
                              setSchoolFilter(school.display_name);
                              setSchoolOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                schoolFilter === school.display_name ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div>
                              <div className="font-medium">{school.name}</div>
                              <div className="text-xs text-muted-foreground">{school.district}</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Exam Year Filter */}
            <div className="flex-1 min-w-[150px]">
              <Select value={examYearFilter} onValueChange={setExamYearFilter}>
                <SelectTrigger data-testid="exam-year-filter-select">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-muted-foreground" />
                    <SelectValue placeholder="All Years" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Exam Years</SelectItem>
                  {EXAM_YEARS.map((year) => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stream Filter */}
            <div className="flex-1 min-w-[150px]">
              <Select value={streamFilter} onValueChange={setStreamFilter}>
                <SelectTrigger data-testid="stream-filter-select">
                  <SelectValue placeholder="All Streams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Streams</SelectItem>
                  {streams.map((stream) => (
                    <SelectItem key={stream.id} value={stream.id}>{stream.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subject Filter */}
            <div className="flex-1 min-w-[150px]">
              <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                <SelectTrigger data-testid="subject-filter-select">
                  <SelectValue placeholder="All Subjects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subjects</SelectItem>
                  {subjects.map((subject) => (
                    <SelectItem key={subject.id} value={subject.id}>{subject.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button onClick={handleFilter} data-testid="apply-filters-btn">
                <Search className="w-4 h-4 mr-2" />
                Filter
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearAllFilters} data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Leaderboard List */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Header Row */}
          <div className="grid grid-cols-12 gap-4 p-4 bg-secondary/50 border-b border-border text-sm font-medium text-muted-foreground">
            <div className="col-span-1">Rank</div>
            <div className="col-span-4">Student</div>
            <div className="col-span-3">School</div>
            <div className="col-span-1 text-center">A/L Year</div>
            <div className="col-span-2 text-right">Score</div>
            <div className="col-span-1 text-right">Streak</div>
          </div>

          {/* Entries */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : entries.length > 0 ? (
            <div className="divide-y divide-border">
              {entries.map((entry, index) => (
                <div 
                  key={entry.user_id}
                  className={`leaderboard-row grid grid-cols-12 gap-4 items-center
                    ${entry.user_id === user?.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                  data-testid={`leaderboard-entry-${index}`}
                >
                  <div className="col-span-1">
                    {getRankBadge(entry.rank)}
                  </div>
                  <div className="col-span-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center font-semibold">
                      {entry.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {entry.name}
                        {entry.user_id === user?.id && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                            You
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-3 text-muted-foreground text-sm truncate">
                    {entry.school || 'Not specified'}
                  </div>
                  <div className="col-span-1 text-center">
                    {entry.exam_year ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary">
                        {entry.exam_year}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </div>
                  <div className="col-span-2 text-right font-bold text-primary">
                    {entry.score.toLocaleString()}
                  </div>
                  <div className="col-span-1 text-right">
                    <div className="inline-flex items-center gap-1 text-orange-500">
                      <Flame className="w-4 h-4" />
                      <span className="font-medium">{entry.streak}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No entries yet</p>
              <p className="text-sm">Be the first to complete a challenge today!</p>
            </div>
          )}
        </div>

        {/* User's Position (if not in top 50) */}
        {user && !entries.find(e => e.user_id === user.id) && entries.length > 0 && (
          <div className="mt-4 p-4 bg-secondary/50 rounded-xl border border-border">
            <p className="text-sm text-muted-foreground text-center">
              Complete more challenges to appear on the leaderboard!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
